import React from 'react';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { ABAS } from '../abas';
import { estadoDosDados } from '../apresentacao';

/**
 * A trilha do diagnóstico, em uma linha.
 *
 * POR QUE EXISTE: o produto entregava telas corretas e deixava o empresário
 * adivinhar o que fazer com elas. Ele lançava três meses, via o índice de saúde
 * e parava — ninguém dizia que faltava o balanço para destravar metade dos
 * indicadores, nem que fechar o mês seguinte é o que transforma um retrato numa
 * série. Consultoria que não diz o próximo passo é relatório.
 *
 * A régua tem três degraus, na ordem em que o valor aparece:
 *   1. lançar o primeiro mês      -> o diagnóstico existe
 *   2. informar o balanço         -> caixa, ciclo e retorno passam a existir
 *   3. fechar o mês seguinte      -> deixa de ser retrato e vira série
 *
 * Ela não é decoração de topo de página: some inteira quando os três degraus
 * estão vencidos, porque aí ela não teria mais nada a ensinar.
 */

const MESES_PARA_TENDENCIA = 6;

const degraus = (leitura) => {
  const estado = estadoDosDados(leitura);
  const meses = estado.meses || 0;

  return [
    {
      id: 'lancar',
      titulo: 'Lançar o primeiro mês',
      feito: estado.temDRE,
      ganho: 'O diagnóstico inteiro aparece: índice de saúde, sinais e plano de ação.',
      acao: 'Lançar agora',
      destino: ABAS.DADOS
    },
    {
      id: 'balanco',
      titulo: 'Informar o balanço',
      feito: estado.temBalanco,
      ganho: estado.bloqueados?.length
        ? `Destrava ${estado.bloqueados.length} ${
            estado.bloqueados.length === 1 ? 'indicador' : 'indicadores'
          }: caixa, ciclo de dinheiro e retorno ao sócio.`
        : 'Destrava caixa, ciclo de dinheiro e retorno ao sócio.',
      acao: 'Completar o balanço',
      destino: ABAS.DADOS
    },
    {
      id: 'serie',
      titulo: `Chegar a ${MESES_PARA_TENDENCIA} meses`,
      feito: meses >= MESES_PARA_TENDENCIA,
      ganho:
        meses > 0
          ? `Você tem ${meses} ${meses === 1 ? 'mês' : 'meses'}. Com ${MESES_PARA_TENDENCIA}, variação vira tendência e a comparação passa a valer.`
          : 'Com meia dúzia de meses, variação vira tendência.',
      acao: 'Fechar o mês',
      destino: ABAS.FECHAR_MES
    }
  ];
};

const ProximoPasso = ({ leitura, onIrPara, onEntrar, autenticado = false }) => {
  const trilha = degraus(leitura);
  const proximo = trilha.find((d) => !d.feito);
  if (!proximo) return null;

  const vencidos = trilha.filter((d) => d.feito).length;
  const progresso = (vencidos / trilha.length) * 100;

  // FECHAR O MÊS EXIGE CONTA — E O CARTÃO TEM DE DIZER ISSO, NÃO SUMIR O BOTÃO.
  //
  // A regra antiga escondia a ação para quem não está conectado, e o resultado
  // era um cartão que anuncia "Próximo passo: chegar a 6 meses" e não oferece
  // caminho nenhum nem explica por quê. Guia que aponta e não deixa andar é
  // pior do que guia nenhum. Agora ele convida a entrar, que é literalmente o
  // próximo passo dessa pessoa.
  //
  // E o botão faz o que o rótulo dele promete: "Entrar para fechar o mês" abre
  // o diálogo de login, e não a tela de Dados. Rótulo que anuncia uma coisa e
  // executa outra é pior do que botão nenhum — a pessoa clica, vai parar em
  // lugar nenhum que ela pediu, e desconfia do resto.
  const precisaDeConta = proximo.destino === ABAS.FECHAR_MES && !autenticado;
  const acaoDoBotao = precisaDeConta ? 'Entrar para fechar o mês' : proximo.acao;
  const aoClicar = () => {
    if (precisaDeConta) {
      if (onEntrar) onEntrar();
      return;
    }
    if (onIrPara) onIrPara(proximo.destino);
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
        // O mesmo raio do cartão de vidro (16px). Em `3` o tema entregava
        // 30px, e a faixa nascia com o dobro do arredondamento dos blocos
        // logo abaixo dela.
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 3 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* SEM SOBRANCELHA ACIMA DO TÍTULO.
              "PRÓXIMO PASSO · 2 de 3" era uma linha em versalete acima de
              "Chegar a 6 meses", repetida no topo de todas as telas: duas
              alturas de texto para uma informação que cabe numa. A contagem
              entra ao lado do próprio título, onde ela é lida junto com o que
              conta — e a palavra "passo" volta a ser dita pelo botão e pela
              trilha, que é onde ela significa algo.
              (Isto não vale para os versaletes que ficam acima de um NÚMERO —
              ÍNDICE DE SAÚDE, LIQUIDEZ CORRENTE. Ali o rótulo não é sobrancelha
              de manchete: é o nome da medida, o recurso nativo do painel.) */}
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {proximo.titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              passo {vencidos + 1} de {trilha.length}
            </Typography>
          </Stack>
          {/* NO CELULAR A FRASE VOLTA, EM DUAS LINHAS.
              Ela tinha sido escondida em `xs` para devolver 206px à primeira
              dobra — e o que sobrava no aparelho era um título, um botão e uma
              barra de 4px sem nada dizendo o que ela media. A sobrancelha que
              saiu logo acima devolveu uma linha inteira; esta frase ocupa duas,
              com reticências, e o guia volta a explicar o que promete. */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: '70ch',
              mt: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: { xs: 2, sm: 'unset' },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {proximo.ganho}
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={aoClicar}
          sx={{ minHeight: 44, flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          {acaoDoBotao}
        </Button>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={progresso}
        sx={{ mt: { xs: 1.25, sm: 1.75 }, height: 4, borderRadius: 999 }}
      />

      {/* A TRILHA VOLTA NO CELULAR. Escondê-la deixava uma barra de progresso
          sem legenda: 4px de preenchimento sem nada nomeando os degraus que ela
          conta. Ela volta com espaçamento menor e quebra de linha, que é o que
          cabe a 390px. */}
      <Stack
        direction="row"
        spacing={{ xs: 1.25, sm: 2 }}
        sx={{ mt: 1, display: 'flex' }}
        flexWrap="wrap"
        useFlexGap
      >
        {trilha.map((degrau) => (
          <Stack key={degrau.id} direction="row" spacing={0.5} alignItems="center">
            {degrau.feito ? (
              <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
            ) : (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  border: '1.5px solid',
                  borderColor: degrau.id === proximo.id ? 'primary.main' : 'divider'
                }}
              />
            )}
            <Typography
              variant="caption"
              color={degrau.feito ? 'text.secondary' : 'text.primary'}
            >
              {degrau.titulo}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default ProximoPasso;
